import { useEffect, useState } from "react";
import { HeartPulse, Plus, Paperclip, Info, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErrorMessage, openStoredFile, formatTanggalID, formatRupiah } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ApprovalChain } from "@/components/ApprovalChain";

function emptyForm() {
  return {
    kategori_id: "",
    nilai_digits: "",
    keterangan: "",
    lampiran_base64: null,
    lampiran_filename: null,
  };
}

function formatRibuan(digits) {
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default function KlaimPengobatan() {
  const [rows, setRows] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maxMb, setMaxMb] = useState(2);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [mine, sal, cfg] = await Promise.all([
        api.get("/klaim/mine"),
        api.get("/klaim/saldo"),
        api.get("/settings/pengajuan"),
      ]);
      setRows(mine.data);
      setSaldo(sal.data);
      setMaxMb(cfg.data.max_lampiran_mb);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) {
      setForm((s) => ({ ...s, lampiran_base64: null, lampiran_filename: null }));
      return;
    }
    if (f.size > maxMb * 1024 * 1024) {
      toast.error(`Ukuran lampiran maksimal ${maxMb}MB`);
      e.target.value = "";
      return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    setForm((s) => ({ ...s, lampiran_base64: b64, lampiran_filename: f.name }));
  }

  function onNilaiChange(e) {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
    setForm((s) => ({ ...s, nilai_digits: digits }));
  }

  const selectedSaldo = saldo?.rows?.find((r) => r.kategori_id === form.kategori_id);
  const nilai = parseInt(form.nilai_digits || "0", 10);

  async function submit() {
    if (!form.kategori_id) {
      toast.error("Kategori pengobatan wajib dipilih");
      return;
    }
    if (!nilai || nilai <= 0) {
      toast.error("Nilai klaim wajib diisi");
      return;
    }
    if (!form.lampiran_base64) {
      toast.error("Lampiran bukti (PDF/JPG) wajib diunggah");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kategori_id: form.kategori_id,
        nilai,
        keterangan: form.keterangan.trim() || null,
        lampiran_base64: form.lampiran_base64,
        lampiran_filename: form.lampiran_filename,
      };
      const { data } = await api.post("/klaim", payload);
      if (data.pesan) toast.info(data.pesan);
      else toast.success("Klaim berhasil diajukan");
      setOpen(false);
      setForm(emptyForm());
      await load();
      window.dispatchEvent(new Event("atlas:refresh-approvals"));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto" data-testid="page-klaim">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Klaim Pengobatan
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Reimbursement Anda
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Ajukan klaim biaya pengobatan sesuai plafon jabatan Anda — pantau sisa saldo per kategori secara real-time.
          </p>
        </div>
        <Button
          onClick={() => { setForm(emptyForm()); setOpen(true); }}
          className="gap-2 bg-teal-700 hover:bg-teal-800"
          data-testid="klaim-add-btn"
        >
          <Plus size={16} /> Ajukan Klaim
        </Button>
      </div>

      {/* Saldo per kategori */}
      {saldo ? (
        <div className="mt-6" data-testid="klaim-saldo-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-teal-700">
              Sisa Plafon — Periode {formatTanggalID(saldo.period_start)} s/d {formatTanggalID(saldo.period_end)}
            </div>
            {saldo.jabatan_nama ? (
              <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid="klaim-jabatan-badge">
                Jabatan: {saldo.jabatan_nama}
              </span>
            ) : null}
          </div>
          {saldo.rows.length === 0 ? (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
              Belum ada kategori pengobatan yang diatur oleh Admin.
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {saldo.rows.map((r) => {
                const pctPakai = r.plafon > 0 ? Math.min(100, (r.terpakai / r.plafon) * 100) : 0;
                return (
                  <div
                    key={r.kategori_id}
                    className="rounded-xl border border-teal-200 bg-teal-50/40 p-4"
                    data-testid={`klaim-saldo-${r.kategori_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Wallet size={14} className="text-teal-700 shrink-0" />
                      <div className="font-semibold text-slate-900 text-sm truncate">{r.kategori_nama}</div>
                    </div>
                    {r.plafon > 0 ? (
                      <>
                        <div className="font-mono-clock text-xl font-bold text-slate-900 mt-2">
                          {formatRupiah(r.tersisa)}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          dari plafon {formatRupiah(r.plafon)}
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-teal-100 overflow-hidden">
                          <div className="h-full bg-emerald-600 transition-all" style={{ width: `${pctPakai}%` }} />
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                          <span>Terpakai: <b className="text-emerald-700">{formatRupiah(r.terpakai)}</b></span>
                          {r.menunggu > 0 ? (
                            <span>Menunggu: <b className="text-amber-700">{formatRupiah(r.menunggu)}</b></span>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">
                        Plafon untuk jabatan Anda belum diatur
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      {/* Riwayat klaim */}
      <div className="mt-6 space-y-4">
        {loading ? (
          <div className="text-center text-slate-500 py-14">Memuat…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white text-center py-14">
            <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
              <HeartPulse size={22} />
            </div>
            <div className="mt-3 font-semibold text-slate-700">Belum ada klaim</div>
            <div className="text-xs text-slate-500 mt-1">
              Klik "Ajukan Klaim" untuk membuat pengajuan reimbursement pertama Anda.
            </div>
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-slate-200 bg-white p-5"
              data-testid={`klaim-card-${r.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                      <HeartPulse size={11} /> {r.kategori_nama}
                    </span>
                    <StatusBadge status={r.status} testId={`klaim-status-${r.id}`} />
                  </div>
                  <div className="mt-2 font-mono-clock text-lg font-bold text-slate-900" data-testid={`klaim-nilai-${r.id}`}>
                    {formatRupiah(r.nilai)}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Diajukan {formatTanggalID(r.tanggal)}
                  </div>
                  {r.keterangan ? (
                    <div className="text-sm text-slate-600 mt-1">{r.keterangan}</div>
                  ) : null}
                  {r.lampiran_path ? (
                    <button
                      onClick={() => openStoredFile(r.lampiran_path).catch(() => toast.error("Gagal membuka lampiran"))}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-800 hover:text-teal-900"
                      data-testid={`klaim-lampiran-${r.id}`}
                    >
                      <Paperclip size={12} /> {r.lampiran_nama || "Lihat bukti"}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
                  Alur Persetujuan
                </div>
                <ApprovalChain chain={r.approval_chain} currentLevel={r.current_level} status={r.status} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Dialog pengajuan klaim */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="klaim-dialog">
          <DialogHeader>
            <DialogTitle>Ajukan Klaim Pengobatan</DialogTitle>
            <DialogDescription>
              Klaim akan diteruskan ke approver (default: Manager SDM &amp; Umum).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kategori Pengobatan</Label>
              <Select value={form.kategori_id} onValueChange={(v) => setForm({ ...form, kategori_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="input-klaim-kategori">
                  <SelectValue placeholder="Pilih kategori…" />
                </SelectTrigger>
                <SelectContent>
                  {(saldo?.rows || []).map((k) => (
                    <SelectItem key={k.kategori_id} value={k.kategori_id}>{k.kategori_nama}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSaldo ? (
                selectedSaldo.plafon > 0 ? (
                  <div className="text-[11px] text-slate-500 mt-1.5" data-testid="klaim-sisa-hint">
                    Sisa saldo: <b className="text-teal-800">{formatRupiah(selectedSaldo.tersisa)}</b>{" "}
                    dari plafon {formatRupiah(selectedSaldo.plafon)}
                  </div>
                ) : (
                  <div className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                    Plafon kategori ini untuk jabatan Anda belum diatur — hubungi Admin/HR.
                  </div>
                )
              ) : null}
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nilai Klaim (Rp)</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">Rp</span>
                <Input
                  inputMode="numeric"
                  className="pl-9 font-mono-clock"
                  placeholder="0"
                  value={formatRibuan(form.nilai_digits)}
                  onChange={onNilaiChange}
                  data-testid="input-klaim-nilai"
                />
              </div>
              {selectedSaldo && selectedSaldo.plafon > 0 && nilai > selectedSaldo.tersisa ? (
                <div className="mt-1.5 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5" data-testid="klaim-over-warning">
                  Nilai melebihi sisa saldo {formatRupiah(selectedSaldo.tersisa)} — pengajuan akan ditolak sistem.
                </div>
              ) : null}
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Keterangan (opsional)</Label>
              <Textarea
                className="mt-1.5"
                rows={2}
                placeholder="Contoh: Pembelian kacamata resep dokter…"
                value={form.keterangan}
                onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                data-testid="input-klaim-keterangan"
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Lampiran Bukti (wajib, PDF/JPG) — maks {maxMb}MB
              </Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="mt-1.5"
                onChange={onFile}
                data-testid="input-klaim-lampiran"
              />
              {!form.lampiran_base64 ? (
                <div className="mt-2 flex items-start gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2.5">
                  <Info size={13} className="mt-0.5 shrink-0" />
                  Unggah kuitansi / bukti pembayaran. Klaim tanpa lampiran tidak dapat diproses.
                </div>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="klaim-cancel-btn">Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="klaim-submit-btn">
              {saving ? "Mengirim…" : "Kirim Klaim"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
